import { Injectable } from '@angular/core';
import { Observable, shareReplay, switchMap } from 'rxjs';
import { MemberBono, PaymentStatus, SessionBono } from '@models';
import { where } from '@angular/fire/firestore';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class SessionBonoService {
  // ─── SessionBono (catalog) ────────────────────────────────────────────────

  /** All session bono products offered by the tenant */
  public allSessionBonos$: Observable<SessionBono[]>;

  /** Only active bono products */
  public activeSessionBonos$: Observable<SessionBono[]>;

  // ─── MemberBono (ownership) ───────────────────────────────────────────────

  /** All member bono instances across all users */
  public allMemberBonos$: Observable<MemberBono[]>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
  ) {
    this.allSessionBonos$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<SessionBono>(
          this.tenantContext.collections.SESSION_BONOS,
        ),
      ),
      shareReplay(1),
    );

    this.activeSessionBonos$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<SessionBono>(
          this.tenantContext.collections.SESSION_BONOS,
          where('active', '==', true),
        ),
      ),
      shareReplay(1),
    );

    this.allMemberBonos$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<MemberBono>(
          this.tenantContext.collections.MEMBER_BONOS,
        ),
      ),
      shareReplay(1),
    );
  }

  // ─── SessionBono CRUD ─────────────────────────────────────────────────────

  async createSessionBono(data: Omit<SessionBono, 'id'>): Promise<string> {
    return this.firestore.create<SessionBono>(
      this.tenantContext.collections.SESSION_BONOS,
      data,
    );
  }

  async updateSessionBono(
    data: Partial<SessionBono> & { id: string },
  ): Promise<void> {
    return this.firestore.update<SessionBono>(
      this.tenantContext.collections.SESSION_BONOS,
      data,
    );
  }

  async deleteSessionBono(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.SESSION_BONOS,
      id,
    );
  }

  // ─── MemberBono CRUD ──────────────────────────────────────────────────────

  /** All bonos owned by a specific user */
  byUser$(userId: string): Observable<MemberBono[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<MemberBono>(
          this.tenantContext.collections.MEMBER_BONOS,
          where('userId', '==', userId),
        ),
      ),
    );
  }

  /** Active bonos (remaining sessions > 0) for a specific user */
  activeByUser$(userId: string): Observable<MemberBono[]> {
    return this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<MemberBono>(
          this.tenantContext.collections.MEMBER_BONOS,
          where('userId', '==', userId),
          where('remainingSessions', '>', 0),
        ),
      ),
    );
  }

  async createMemberBono(data: Omit<MemberBono, 'id'>): Promise<string> {
    return this.firestore.create<MemberBono>(
      this.tenantContext.collections.MEMBER_BONOS,
      data,
    );
  }

  async updateMemberBono(
    data: Partial<MemberBono> & { id: string },
  ): Promise<void> {
    return this.firestore.update<MemberBono>(
      this.tenantContext.collections.MEMBER_BONOS,
      data,
    );
  }

  /** Deduct one session from a MemberBono */
  async useSession(memberBonoId: string, current: MemberBono): Promise<void> {
    return this.firestore.update<MemberBono>(
      this.tenantContext.collections.MEMBER_BONOS,
      {
        id: memberBonoId,
        remainingSessions: current.remainingSessions - 1,
      },
    );
  }

  async updatePaymentStatus(
    id: string,
    paymentStatus: PaymentStatus,
  ): Promise<void> {
    return this.firestore.update<MemberBono>(
      this.tenantContext.collections.MEMBER_BONOS,
      { id, paymentStatus },
    );
  }

  async deleteMemberBono(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.MEMBER_BONOS,
      id,
    );
  }
}
